import React, { useState, useEffect } from 'react';


// React Chart Component
import { AgCharts } from 'ag-charts-react';
import { time } from "ag-charts-community"
import { HMChalet, IGraphArgs } from '../data/chaletapi';
import { Button, ButtonGroup, Col, Container, Row } from 'react-bootstrap';
import chaletStore from '../state/chaletstore';

/** what is shown: a room (indoor temperature) or a VBUS sensor */
type Source = { kind: 'room' | 'device', id: string }

// VBUS sensors; ids are the device names in InfluxDB
const SENSORS = [
  { id: 'Outside', name: 'Outside' },
  { id: 'BoilerTop', name: 'Boiler (top)' },
  { id: 'BoilerBottom', name: 'Boiler (bottom)' },
  { id: 'FirePlace', name: 'Fireplace' },
]

// interval = distance between axis labels, chosen for ~6-13 labels so they do not overlap
const timeRanges = {
  '1h': { id: '1h',start:'-1h', name: '1 h',every:'10s',interval: time.minute.every(10), format: '%H:%M'},
  '24h': { id: '24h',start:'-24h',name: '24 h',every:'1m',interval: time.hour.every(3), format: '%H:%M'},
  '7d': { id: '7d',start:'-7d',name: '7 d',every:'30m',interval: time.day, format: '%a %d.%m'},
  '30d': { id: '30d',start:'-30d',name: '30 d',every:'4h',interval: time.day.every(5), format: '%d.%m'},
  '3M': { id: '3M',start:'-3mo',name: '3 M',every:'3h',interval: time.monday, format: '%d.%m'},
  '6M': { id: '6M',start:'-6mo',name: '6 M',every:'6h',interval: time.month, format: '%d.%m'},
  '1Y': { id: '1Y',start:'-1y',name: '1 Y',every:'1d',interval: time.month, format: '%m.%Y'}
}
type TimeRangeId = keyof typeof timeRanges

export const HistoryPage = () => {
  const rooms = chaletStore(state => state.data.rooms)
  const isInitialized = chaletStore(state => state.data.isInitilized)
  const fetchInitialState = chaletStore(state => state.fetchInitialState)
  const [chartOptions, setChartOptions] = useState({});
  const [source, setSource] = useState<Source>({ kind: 'room', id: 'ParentsRoom' });
  const [timeRangeId, setTimeRangeId] = useState<TimeRangeId>('24h');

  // the room list comes from the store, which is otherwise only loaded on the home page
  useEffect(() => {
    if (!isInitialized) {
      fetchInitialState()
    }
  }, []);

  // reload whenever the source or the time range changes
  useEffect(() => {
    fetchInfo(source, timeRangeId)
  }, [source, timeRangeId]);

  const isActive = (kind: Source['kind'], id: string) => source.kind === kind && source.id === id

  const fetchInfo = (source: Source, timeRangeId: TimeRangeId) => {
    const { start, every, interval, format } = timeRanges[timeRangeId]
    const args: IGraphArgs = source.kind === 'room'
      ? { room: source.id, start, every }
      : { device: source.id, start, every }
    // rooms on a fixed indoor scale; the sensors range from below zero to ~90 °C
    const range = source.kind === 'room' ? { min: 6, max: 30 } : {}
    return HMChalet.getGraphData(args)
      .then((res: any) => {
        const newdata = []
        for (const timedata of res.data) {
          newdata.push({ timestamp: Date.parse(timedata.timestamp), value: timedata.value })
        }
        const chartdata = {
          // Data: Data to be displayed in the chart
          data: newdata,
          theme: {
            overrides: {
              line: {
                series: {
                  interpolation: {
                    type: "smooth",
                    position: "middle",
                  },
                  marker: {
                    enabled: false,
                  },
                },
              },
            },
          },
          // Series: Defines which chart type and data to use
          axes: [
            {
              type: 'time',
              position: "bottom",
              nice: false,
              interval: { step: interval },
              label: { format: format }
            },
            {
              position: "right",
              type: "number",
              ...range
            },{
              position: "left",
              type: "number",
              ...range
            }
          ],
          series: [{ type: 'line', xKey: 'timestamp', yKey: 'value', stageKey: null, valueKey: null }],
        } as any
        setChartOptions(chartdata)
      })
  }

  return (
    <Container fluid className="pt-5 pb-5 h-100">
      <Row className="justify-content-center pb-5 h-100">
        <Col className="col-lg-1 col-xs-auto h-100">
          <ButtonGroup vertical>
            {Object.values(rooms).map((room) => (
              <Button key={room.id} active={isActive('room', room.id)}
                onClick={() => setSource({ kind: 'room', id: room.id })}>{room.name}
              </Button>
            ))}
          </ButtonGroup>
          <ButtonGroup vertical className="mt-3">
            {SENSORS.map((sensor) => (
              <Button key={sensor.id} variant="secondary" active={isActive('device', sensor.id)}
                onClick={() => setSource({ kind: 'device', id: sensor.id })}>{sensor.name}
              </Button>
            ))}
          </ButtonGroup>
        </Col>
        <Col className="col-lg-11 col-xs-auto h-100">
          <Container fluid className="pb-5 h-100">
            <Row >
              <Col>
                <ButtonGroup>
                {Object.values(timeRanges).map((timeRange) => (
                  <Button key={timeRange.id} active={timeRange.id === timeRangeId}
                    onClick={() => setTimeRangeId(timeRange.id as TimeRangeId)}>{timeRange.name}</Button>
                ))}
                </ButtonGroup>
              </Col>
            </Row>
            <Row className="h-100">
              <Col className="h-100">
                <div style={{ display: 'grid', width: '100%', height: '100%' }}>
                  <AgCharts options={chartOptions} />
                </div>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  )
};
